// src/services/authApi.js
import { getStoredReferralCode } from './referralStorage';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api'; // Fallback

// Function to handle common fetch logic and error handling
async function handleResponse(response) {
    const contentType = response.headers.get('content-type');
    let data;
    if (contentType && contentType.includes('application/json')) {
        data = await response.json();
    } else {
        data = { error: `Unexpected response type: ${contentType}`, responseText: await response.text() }; // Capture text for non-JSON
    }

    if (!response.ok) {
        const error = new Error(data.error || data.message || `HTTP error! status: ${response.status}`); // Use data.message as well
        error.status = response.status;
        error.data = data;
        throw error;
    }
    return data;
}


// --- Username/Password Flow ---

export const loginUser = async (email, password) => {
    // Login likely still uses email if alias login is enabled in Cognito
    const response = await fetch(`${API_URL}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email, password }), // Send email for login
    });
    return handleResponse(response); // Returns tokens on success
};

export const signupUser = async (username, email, password) => { // Accept username
    const body = { username, email, password }; // Include username in body

    const response = await fetch(`${API_URL}/auth/signup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body), // Send username, email, password
    });
    // *** Expect backend to return the actual username used ***
    return handleResponse(response); // Returns message, userSub, username
};

export const confirmSignup = async (username, code) => {
    // Forward any captured referral code so the backend can link this new
    // user to their referrer at the moment the account is first created.
    const referralCode = getStoredReferralCode();
    const body = { username, code };
    if (referralCode) body.referral_code = referralCode;

    const response = await fetch(`${API_URL}/auth/confirm-signup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
    });

    return handleResponse(response);
};

export const resendConfirmationCode = async (username) => {
    const response = await fetch(`${API_URL}/auth/resend-code`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username }), // Send actual username
    });
     return handleResponse(response); // Returns message
};

export const fetchUserProfile = async (token) => {
    if (!token) throw new Error("No token provided for fetchUserProfile");
    const response = await fetch(`${API_URL}/auth/me`, { // Assuming '/api/auth/me' exists
        method: 'GET',
        headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
         },
    });
    return handleResponse(response); // Returns user profile data
};

export const redirectToGoogleLogin = () => {
    localStorage.setItem('pendingLoginProvider', 'google');
    // Forward captured referral code via `?ref=` so the backend OAuth handler
    // can round-trip it through Cognito's `state` parameter.
    const referralCode = getStoredReferralCode();
    const url = new URL(`${API_URL}/oauth/login-google`);
    if (referralCode) url.searchParams.set('ref', referralCode);
    window.location.href = url.toString();
};

// Function to redirect user to Amazon login
export const redirectToAmazonLogin = () => {
    // Store that we're using Amazon login
    localStorage.setItem('pendingLoginProvider', 'amazon');
    const amazonLoginUrl = `${API_URL}/auth/amazon/login`;
    window.location.href = amazonLoginUrl;
};

// FIXED: New logout function that calls the API first, then redirects
export const logoutUser = async (token) => {
    if (!token) {
        return { message: "No token to logout" };
    }

    try {
        const response = await fetch(`${API_URL}/auth/logout`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
            }
        });
        return handleResponse(response);
    } catch (error) {
        // Don't throw error, continue with logout process
        return { message: "Logout API failed but continuing" };
    }
};

// FIXED: Updated redirect function that handles both API logout and Cognito redirect
export const redirectToLogout = async () => {
    const token = localStorage.getItem('authToken');
    const loginProvider = localStorage.getItem('loginProvider');
    
    // Step 1: Call logout API if we have a token
    if (token) {
        try {
            await logoutUser(token);
        } catch (error) {
        }
    }
    
    // Step 2: Clear local storage
    localStorage.removeItem('authToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('idToken');
    localStorage.removeItem('authUser');
    localStorage.removeItem('loginProvider');
    localStorage.removeItem('pendingLoginProvider');
    localStorage.removeItem('sessionStartTime');
    
    // Step 3: Redirect based on provider
    if (loginProvider === 'amazon' || loginProvider === 'google') {
        // For OAuth users, redirect to Cognito logout which will handle the full logout flow
        window.location.href = `${API_URL}/auth/logout-redirect`;
    } else {
        // For email/password users, just redirect to auth page
        window.location.href = '/auth';
    }
};

// --- Forgot Password Flow ---

export const requestPasswordReset = async (email) => {
    const response = await fetch(`${API_URL}/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }), // Send email as identifier
    });
    return handleResponse(response); // Returns { message: "..." } on success
};

export const confirmPasswordReset = async (email, code, newPassword) => {
    const response = await fetch(`${API_URL}/auth/confirm-forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, code, newPassword }),
    });
    return handleResponse(response); // Returns { message: "..." } on success
};

export const changePassword = async (token, currentPassword, newPassword, email) => {
    if (!token) throw new Error("No token provided for changePassword");
    
    // Validate email before making the request
    if (!email || email === 'N/A') {
        const error = new Error("Email address is required.");
        error.data = { error: "Email address is required." };
        throw error;
    }
    
    const response = await fetch(`${API_URL}/auth/change-password`, { // Updated endpoint
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`, // Send token in header
        },
        body: JSON.stringify({ 
            currentPassword, 
            newPassword,
            email // Include email in the request
        }),
    });
    return handleResponse(response); // Returns { message: "..." } on success
};

// Mark the in-app tutorial as seen for the current user. Called when the user
// either starts or dismisses the tutorial intro modal so they only see it once.
export const markTutorialSeen = async (token) => {
    if (!token) throw new Error("No token provided for markTutorialSeen");
    const response = await fetch(`${API_URL}/auth/mark-tutorial-seen`, {
        method: 'POST',
        headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
        },
        body: JSON.stringify({ tutorial_seen: true })
    });
    return handleResponse(response);
};

// Function to save onboarding data (for onboarding form/details)
export const saveOnboardingData = async (onboardingData) => {
    const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
    const response = await fetch(`${API_URL}/auth/handle-onboarding-details`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(onboardingData)
    });

    return handleResponse(response);
};

// --- Referral Code Functions ---

/**
 * Apply a referral code to grant bonus credits and tokens
 * @param {string} token - JWT access token
 * @param {string} referralCode - The referral code to apply
 * @returns {Promise} - Response with success status and bonus details
 */
export const applyReferralCode = async (token, referralCode) => {
    if (!token) throw new Error("No token provided for applyReferralCode");
    if (!referralCode) throw new Error("No referral code provided");

    const response = await fetch(`${API_URL}/auth/apply-referral-code`, {
        method: 'POST',
        headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
        },
        body: JSON.stringify({ referral_code: referralCode })
    });
    return handleResponse(response);
};

/**
 * Check if the current user is eligible for a referral bonus
 * @param {string} token - JWT access token
 * @returns {Promise} - Response with eligibility status
 */
export const checkReferralEligibility = async (token) => {
    if (!token) throw new Error("No token provided for checkReferralEligibility");

    const response = await fetch(`${API_URL}/auth/check-referral-eligibility`, {
        method: 'GET',
        headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
        }
    });
    return handleResponse(response);
};